import { useEffect, useState } from "react";
import { Info, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { quarterRange } from "@/lib/format";
import { DateRange, Money, PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";

function Row({ box, label, amount, vat, strong, testid }) {
  return (
    <tr data-testid={testid} className={strong ? "bg-slate-50 font-bold" : ""}>
      <td className="w-16"><span className="inline-flex h-7 min-w-[2.5rem] items-center justify-center rounded bg-[#0B1220] px-2 font-mono text-xs font-bold text-white">{box}</span></td>
      <td>{label}</td>
      <td className="text-end">{amount !== undefined && <Money value={amount} cur={false} />}</td>
      <td className="text-end">{vat !== undefined && <Money value={vat} cur={false} />}</td>
    </tr>
  );
}

function Head({ title, color }) {
  const { t } = useLang();
  return (
    <thead>
      <tr><th colSpan={4} className={`!py-3 !text-sm !normal-case !tracking-normal !text-white ${color}`}>{title}</th></tr>
      <tr><th /><th /><th className="text-end">{t("amountAed")}</th><th className="text-end">{t("vatAed")}</th></tr>
    </thead>
  );
}

export default function Vat201() {
  const { t } = useLang();
  const [range, setRange] = useState(quarterRange());
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/vat/return", { params: range }).then((r) => setD(r.data)); }, [range]);
  const letters = "abcdefg";

  return (
    <div data-testid="vat-page">
      <PageHeader title={t("vat201")} subtitle={t("vatSubtitle")}
        actions={<Button variant="outline" className="no-print" data-testid="vat-print-btn" onClick={() => window.print()}><Printer className="me-1 h-4 w-4" />{t("print")}</Button>} />
      <Panel className="no-print mb-6 flex flex-wrap items-end gap-4 p-5">
        <div className="flex gap-1">
          {[-1, 0].map((o) => (
            <button key={o} data-testid={`vat-quarter-${o === 0 ? "current" : "previous"}`} onClick={() => setRange(quarterRange(o))} className="rounded-full border border-slate-200 px-3 py-1.5 font-mono text-xs font-semibold hover:bg-slate-100">
              {t("quarter")} {quarterRange(o).start.slice(0, 7)}
            </button>
          ))}
        </div>
        <DateRange testid="vat-range" start={range.start} end={range.end} onChange={(start, end) => setRange({ start, end })} />
      </Panel>
      {d && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="space-y-6 xl:col-span-2">
            <Panel className="overflow-x-auto">
              <table className="data-table">
                <Head title={t("vatOnSales")} color="bg-[#007A3D]" />
                <tbody>
                  {d.emirates.map((e, i) => <Row key={e.emirate} testid={`vat-box-1${letters[i]}`} box={`1${letters[i]}`} label={`${t("box1")} ${t(`em_${e.emirate}`)}`} amount={e.amount} vat={e.vat} />)}
                  <Row box="2" label={t("box2")} amount={0} vat={0} />
                  <Row box="3" label={t("box3")} amount={0} vat={0} />
                  <Row box="4" label={t("box4")} amount={d.zero_rated} testid="vat-box-4" />
                  <Row box="5" label={t("box5")} amount={d.exempt} testid="vat-box-5" />
                  <Row box="6" label={t("box6")} amount={0} vat={0} />
                  <Row box="7" label={t("box7")} amount={0} vat={0} />
                  <Row box="8" label={t("box8")} amount={d.box8.amount} vat={d.box8.vat} strong testid="vat-box-8" />
                </tbody>
              </table>
            </Panel>
            <Panel className="overflow-x-auto">
              <table className="data-table">
                <Head title={t("vatOnExpenses")} color="bg-[#CE1126]" />
                <tbody>
                  <Row box="9" label={t("box9")} amount={d.box9.amount} vat={d.box9.vat} testid="vat-box-9" />
                  <Row box="10" label={t("box10")} amount={0} vat={0} />
                  <Row box="11" label={t("box11")} amount={d.box11.amount} vat={d.box11.vat} strong testid="vat-box-11" />
                </tbody>
              </table>
            </Panel>
          </div>
          <div className="space-y-4">
            <Panel className="overflow-hidden">
              <div className="bg-[#0B1220] px-5 py-3 font-display font-bold text-white">{t("netVatDue")}</div>
              <div className="space-y-3 p-5 text-sm">
                <div className="flex justify-between gap-3"><span className="text-slate-600"><b className="font-mono">12</b> · {t("box12")}</span><Money value={d.box12} testid="vat-box-12" /></div>
                <div className="flex justify-between gap-3"><span className="text-slate-600"><b className="font-mono">13</b> · {t("box13")}</span><Money value={d.box13} testid="vat-box-13" /></div>
                <div className={`flex justify-between gap-3 rounded-lg p-3 text-base font-bold ${d.box14 >= 0 ? "bg-[#FFFBEB] text-[#7A5F10]" : "bg-emerald-50 text-[#007A3D]"}`}>
                  <span><b className="font-mono">14</b> · {t("box14")}</span><Money value={d.box14} testid="vat-box-14" />
                </div>
              </div>
            </Panel>
            <div className="flex gap-2 rounded-lg border border-slate-200 bg-white p-4 text-xs text-slate-500"><Info className="h-4 w-4 shrink-0" />{t("ftaNote")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
